'use client';

import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { OrderBookSnapshot } from '@/lib/api/order-book/types';
import { OrderBookService } from '@/lib/services/OrderBookService';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import type { OrderBookResponse } from '@/lib/types/markets/order-book';

const OrderBookStoreContext = createContext<Record<string, OrderBookService> | undefined>(
  undefined,
);

type OrderBookStoreProviderProps = {
  children: ReactNode;
  /** SSR order book data in snapshot format (preferred) */
  initialOrderBooks?: Record<string, OrderBookSnapshot>;
  /** Outcome IDs to create services for (preferred over relying on store) */
  outcomeIds?: string[];
  /** Legacy prop for backward compatibility - converted to initialOrderBooks internally */
  orderBooksById?: Record<string, OrderBookResponse>;
};

const OrderBookStoreProviderInner = ({
  children,
  initialOrderBooks,
  outcomeIds: outcomeIdsProp,
  orderBooksById: _orderBooksById,
}: OrderBookStoreProviderProps) => {
  // Note: orderBooksById is kept for backward compatibility but is not used for initialization
  // since the old implementation didn't actually use it either (services were initialized via WebSocket)
  // Store services in state so that React propagates a new context value
  // whenever we create new services for outcomes.
  const [services, setServices] = useState<Record<string, OrderBookService>>({});
  const event = useOrderFormStore.use.event();

  // CRITICAL FIX: Use outcomeIds from props if available (for SSR hydration),
  // fallback to event from store for backward compatibility
  const outcomeIdsKey = useMemo(() => {
    // Prefer props - they are available immediately at SSR time
    if (outcomeIdsProp && outcomeIdsProp.length > 0) {
      return outcomeIdsProp.sort().join(',');
    }
    // Fallback to store (may be undefined on first render before ServerToStore hydrates)
    const ids = event?.markets?.map((m) => m.id) ?? [];
    return ids.sort().join(',');
  }, [outcomeIdsProp, event?.markets]);

  // CRITICAL FIX: Track previous key to avoid unnecessary updates
  const prevKeyRef = useRef<string>('');

  // Track services synchronously to handle React StrictMode double-invocation
  const servicesRef = useRef<Record<string, OrderBookService>>({});

  // Create services for all outcomes from event markets
  // If SSR data is available, initialize services with it
  useEffect(() => {
    // Skip if outcomeIdsKey hasn't actually changed
    if (prevKeyRef.current === outcomeIdsKey) {
      return;
    }

    prevKeyRef.current = outcomeIdsKey;

    if (!outcomeIdsKey) {
      return;
    }

    const outcomeIds = outcomeIdsKey.split(',').filter(Boolean);
    if (outcomeIds.length === 0) {
      return;
    }

    // Check if we already have all services (using ref for synchronous check)
    const allExist = outcomeIds.every((id) => servicesRef.current[id]);
    if (allExist) {
      return;
    }

    let hasChanges = false;

    outcomeIds.forEach((outcomeId) => {
      // Create service only if it doesn't exist yet (check ref synchronously)
      if (!servicesRef.current[outcomeId]) {
        const service = new OrderBookService(outcomeId);

        // Initialize with SSR data if available
        const ssrData = initialOrderBooks?.[outcomeId];

        if (ssrData) {
          service.initializeFromSnapshot(ssrData);
        }

        // Update ref synchronously to prevent double-creation in StrictMode
        servicesRef.current[outcomeId] = service;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      // Trigger re-render with new services
      setServices({ ...servicesRef.current });
    }
  }, [outcomeIdsKey, initialOrderBooks]);

  // Memoize context value to avoid re-renders when object identity is unchanged
  const contextValue = useMemo(() => services, [services]);

  return (
    <OrderBookStoreContext.Provider value={contextValue}>{children}</OrderBookStoreContext.Provider>
  );
};

// CRITICAL FIX: Memoize the provider to prevent re-renders when parent re-renders
// This prevents the infinite loop of provider re-renders → state reset → re-renders
export const OrderBookStoreProvider = memo(OrderBookStoreProviderInner);

export const useOrderBooksServices = (): Record<string, OrderBookService> => {
  const orderBookStoreContext = useContext(OrderBookStoreContext);
  if (!orderBookStoreContext) {
    throw new Error(`useOrderBookServices must be used within OrderBookStoreProvider`);
  }
  return orderBookStoreContext;
};
